import React, { useState, useEffect, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Grid, Container, Typography } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

import axiosInstance from '../api';
import { PieChart, RegistrationInfo, AppWidgetSummary, MiniAppWidgetSummary, LoginWidgetSummary } from '../sections/dashboard';
import { authContext } from '../routes';
import SERVER_API_URL from '../config';

const StyledLink = styled(Typography)(() => ({
  textDecoration: 'none',
  marginLeft: 2,
  display: 'block',
  color: 'inherit',
  transition: 'color 0.2s',
  '&:hover': { cursor: 'pointer', color: 'teal' },
}));

export default function DashboardAppPage() {
  const { user } = useContext(authContext);
  const [categoryList, setCategoryList] = useState([]);
  const [churchList, setChurchList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [allActiveUsersLength, setAllActiveUsersLength] = useState([]);
  const [allBlockedUsersLength, setAllBlockedUsersLength] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      const expirationDate = new Date(decodedToken.exp * 1000);
      if (expirationDate < new Date()) {
        localStorage.clear();
        navigate('/login');
      } else if (user && !user.role.permissions.churchLevel.read) {
        navigate('/dashboard/payment');
      }
    } else {
      navigate('/login');
    }
  }, [navigate, user]);

  const dioceseLevel = user && user.role.permissions.dioceseLevel.read;
  const districtLevel = user && user.role.permissions.districtLevel.read;
  const churchLevel = user && user.role.permissions.churchLevel.read;

  useEffect(() => {
    if (!churchLevel && !districtLevel && dioceseLevel) {
      navigate('/dashboard/payment');
    }
  }, [navigate, churchLevel, districtLevel, dioceseLevel]);

  const countAllActiveAndBlockedUsersLength = (allUsers) => {
    const users = { activeLength: 0, blockedLength: 0 };
    allUsers.forEach((user) => {
      if (user.status === 'ንቁ') {
        users.activeLength += 1;
      } else if (user.status === 'ታግዷል') {
        users.blockedLength += 1;
      }
    });
    setAllActiveUsersLength(users && users.activeLength);
    setAllBlockedUsersLength(users && users.blockedLength);
  };

  useEffect(() => {
    const getAllCategory = async () => {
      try {
        let response;
        if (dioceseLevel) {
          response = await axiosInstance.get(`${SERVER_API_URL}/category/findAll`);
        } else if (districtLevel) {
          response = await axiosInstance.get(
            `${SERVER_API_URL}/category/findManyByDistrictId/${user && user.church.district.id}`
          );
        } else {
          response = await axiosInstance.get(`${SERVER_API_URL}/category/findManyByChurchId/${user && user.church.id}`);
        }
        if (response.status === 200) {
          setCategoryList(response.data);
        }
      } catch (error) {
        // console.log(error);
      }
    };

    const getAllChurch = async () => {
      try {
        let response;
        if (dioceseLevel) {
          response = await axiosInstance.get(`${SERVER_API_URL}/church/findAllDashboard`);
        } else if (districtLevel) {
          response = await axiosInstance.get(
            `${SERVER_API_URL}/church/findManyByDistrictId/${user && user.church.district.id}`
          );
        }
        if (response.status === 200) {
          setChurchList(response.data);
        }
      } catch (error) {
        // console.log(error);
      }
    };

    const getAllDistrict = async () => {
      try {
        const response = await axiosInstance.get(`${SERVER_API_URL}/district/findAllDashboard`);

        if (response.status === 200) {
          setDistrictList(response.data);
        }
      } catch (error) {
        // console.log(error);
      }
    };

    const getAllUsers = async () => {
      try {
        let response;
        if (dioceseLevel) {
          response = await axiosInstance.post(`${SERVER_API_URL}/user/findAllForDashboard`);
        } else if (districtLevel) {
          response = await axiosInstance.post(
            `${SERVER_API_URL}/user/findManyByDistrictId/${user && user.church.district.id}`
          );
        } else {
          response = await axiosInstance.post(`${SERVER_API_URL}/user/findManyByChurchId/${user && user.church.id}`);
        }

        if (response.status === 200) {
          countAllActiveAndBlockedUsersLength(response.data);
        }
      } catch (error) {
        // console.log(error);
      }
    };

    getAllCategory();
    getAllChurch();
    getAllDistrict();
    getAllUsers();
  }, [dioceseLevel, districtLevel, user]);

  const colors = categoryList.map((category) => category.color);
  const categoryData = categoryList.map((category) => ({
    label: category.name,
    value: category.users.length,
  }));


  const handleChurchAdminManual = () => {
    const link = document.createElement('a');
    link.href = '/manual/churchAdminManual.pdf';
    link.download = 'churchAdminManual.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDioceseAdminManual = () => {
    const link = document.createElement('a');
    link.href = '/manual/dioceseAdminManual.pdf';
    link.download = 'dioceseAdminManual.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Helmet>
        <title> ዳሽቦርድ | የሰ/ሸ/ሃ/መ/አ/ስርዓት </title>
      </Helmet>

      <Container maxWidth="xl">
        {churchLevel && (
          <Grid container spacing={3}>
            {dioceseLevel && (
              <Grid item xs={12} sm={dioceseLevel ? 6 : 4.5} md={3}
                lg={dioceseLevel ? 2.25 : districtLevel ? 3 : 4.5}
              >
                <AppWidgetSummary
                  title="ጠቅላላ ወረዳዎች"
                  total={districtList && districtList.length}
                  icon={'fa-solid:map-marked-alt'}
                />
              </Grid>
            )}
            {districtLevel && (
              <Grid item xs={12} sm={dioceseLevel ? 6 : districtLevel ? 4 : 3} md={dioceseLevel ? 3 : districtLevel ? 4 : 3}
                lg={dioceseLevel ? 2.25 : districtLevel ? 3 : 4.5}
              >
                <AppWidgetSummary
                  title="ጠቅላላ አብያተ ክርስቲያናት"
                  total={churchList && churchList.length}
                  color="success"
                  icon={'mdi:church'}
                />
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sm={dioceseLevel ? 6 : districtLevel ? 4 : 6}
              md={dioceseLevel ? 3 : districtLevel ? 4 : 6}
              lg={dioceseLevel ? 2.25 : districtLevel ? 3 : 4.5}
            >
              <AppWidgetSummary
                title="ንቁ ተጠቃሚዎች"
                total={allActiveUsersLength}
                color="warning"
                icon={'fluent:people-team-16-filled'}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={dioceseLevel ? 6 : districtLevel ? 4 : 6}
              md={dioceseLevel ? 3 : districtLevel ? 4 : 6}
              lg={dioceseLevel ? 2.25 : districtLevel ? 3 : 4.5}
            >
              <AppWidgetSummary
                title="የታገዱ ተጠቃሚዎች"
                total={allBlockedUsersLength}
                color="error"
                icon={'ant-design:usergroup-delete'}
              />
            </Grid>

            <Grid
              item
              xs={12}
              lg={3}
            >
              <LoginWidgetSummary />
            </Grid>

            <Grid item xs={12}>
              <MiniAppWidgetSummary />
            </Grid>

            <Grid item xs={12} md={7} lg={8}>
              <RegistrationInfo />
            </Grid>
            <Grid item xs={12} md={5} lg={4}>
              <PieChart title="ምድቦች" subheader="የተጠቃሚዎች መቶኛ በየምድባቸው።" chartData={categoryData} chartColors={colors} />
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography color="text.secondary" display="flex" justifyContent="center" gutterBottom sx={{ mt: 5 }}>
            &copy; ሰሜን ሸዋ ሀገረ ስብከት
            {dioceseLevel && (
              <StyledLink
                onClick={handleDioceseAdminManual}
                sx={{ textDecoration: 'none' }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {' | '} የተጠቃሚ መመሪያ አውርድ
              </StyledLink>
            )}
            {!dioceseLevel && !districtLevel && churchLevel && (
              <StyledLink
                onClick={handleChurchAdminManual}
                style={{ textDecoration: 'none' }}
                target="_blank"
                rel="noopener noreferrer"
              >

                {' | '} የተጠቃሚ መመሪያ አውርድ
              </StyledLink>
            )}
          </Typography>
        </Grid>
      </Container>
    </>
  );
}
